<script setup>
import { computed, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import SearchAPI from 'dashboard/api/search';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  // Label shown before the list loads, e.g. "#42 · Maria Silva"
  displayLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const options = ref([]);

const toOption = conversation => ({
  label: `#${conversation.id} · ${conversation.contact?.name || ''}`,
  value: conversation.id,
});

const comboboxOptions = computed(() => {
  const list = [...options.value];

  // Keep the linked conversation visible even when it is not in the loaded
  // results (e.g. it is outside the last fifteen recent conversations).
  if (
    props.modelValue &&
    !list.some(option => option.value === Number(props.modelValue))
  ) {
    list.unshift({
      label: props.displayLabel,
      value: Number(props.modelValue),
    });
  }

  return list;
});

const searchConversations = async query => {
  try {
    const {
      data: { payload },
    } = await SearchAPI.conversations({ q: query.trim() });
    options.value = (payload?.conversations || []).map(toOption);
  } catch {
    options.value = [];
  }
};

// Fetch lazily, only when the dropdown opens, instead of on mount.
const handleOpen = () => {
  searchConversations('');
};

const handleSearch = useDebounceFn(query => {
  searchConversations(query || '');
}, 300);

const handleSelect = value => {
  emit('update:modelValue', value ? Number(value) : '');
};
</script>

<template>
  <ComboBox
    :model-value="modelValue"
    :options="comboboxOptions"
    :display-label="displayLabel"
    :placeholder="t('KANBAN.CARDS.CONVERSATION_PLACEHOLDER')"
    :search-placeholder="t('KANBAN.CARDS.CONVERSATION_SEARCH_PLACEHOLDER')"
    :empty-state="t('KANBAN.CARDS.CONVERSATION_EMPTY')"
    use-api-results
    @open="handleOpen"
    @search="handleSearch"
    @update:model-value="handleSelect"
  />
</template>
